import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import {
  Users, Search, RefreshCw, ChevronLeft, ChevronRight, Mail, AlertTriangle,
  CheckCircle2, Clock, Activity, Eye, ArrowUpDown, ArrowUp, ArrowDown,
  Filter, FolderOpen, FileText, Loader2, X, ExternalLink, Send,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const statusColors = {
  completed: "bg-emerald-100 text-emerald-700",
  in_progress: "bg-blue-100 text-blue-700",
  issue_flagged: "bg-red-100 text-red-700",
};

const activityLevel = (lastUpdate) => {
  if (!lastUpdate) return { label: "No Activity", color: "bg-slate-300", ring: "" };
  const diff = (Date.now() - new Date(lastUpdate).getTime()) / (1000 * 60 * 60);
  if (diff < 24) return { label: "Active", color: "bg-emerald-500", ring: "ring-2 ring-emerald-200" };
  if (diff < 72) return { label: "Recent", color: "bg-amber-500", ring: "ring-2 ring-amber-200" };
  return { label: "Idle", color: "bg-slate-400", ring: "" };
};

export default function ResourceOps() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [sortBy, setSortBy] = useState("total_tasks");
  const [sortDir, setSortDir] = useState("desc");

  // Detail view
  const [selectedResource, setSelectedResource] = useState(null);
  const [detailTasks, setDetailTasks] = useState([]);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailFilter, setDetailFilter] = useState({ project: "", status: "", type: "" });
  const [detailSearch, setDetailSearch] = useState("");

  // Email
  const [showEmail, setShowEmail] = useState(false);
  const [emailTarget, setEmailTarget] = useState(null);
  const [emailSubject, setEmailSubject] = useState("");
  const [emailBody, setEmailBody] = useState("");
  const [sendingEmail, setSendingEmail] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      const res = await axios.get(`${API}/tracker/admin/resource-centre`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      setData(res.data);
    } catch { toast.error("Failed to load resource data"); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const fetchResourceTasks = async (email) => {
    setDetailLoading(true);
    try {
      const token = localStorage.getItem("token");
      const res = await axios.get(`${API}/resource-center/${encodeURIComponent(email)}/tasks?limit=500`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      setDetailTasks(res.data.tasks || []);
    } catch { toast.error("Failed to load tasks"); }
    finally { setDetailLoading(false); }
  };

  const openDetail = (resource) => {
    setSelectedResource(resource);
    setDetailFilter({ project: "", status: "", type: "" });
    setDetailSearch("");
    fetchResourceTasks(resource.email);
  };

  const openEmailDialog = (resource) => {
    setEmailTarget(resource);
    setEmailSubject(`Regarding your assigned projects — Graphikpro`);
    setEmailBody(`Hi ${resource.name},\n\n`);
    setShowEmail(true);
  };

  const handleSendEmail = async () => {
    if (!emailTarget) return;
    setSendingEmail(true);
    try {
      const token = localStorage.getItem("token");
      await axios.post(`${API}/resource-center/send-resource-email`, {
        resource_email: emailTarget.email,
        subject: emailSubject,
        body: emailBody,
      }, { headers: token ? { Authorization: `Bearer ${token}` } : {} });
      toast.success(`Email sent to ${emailTarget.name}`);
      setShowEmail(false);
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to send email"); }
    finally { setSendingEmail(false); }
  };

  // Sorting
  const toggleSort = (field) => {
    if (sortBy === field) setSortDir(d => d === "desc" ? "asc" : "desc");
    else { setSortBy(field); setSortDir("desc"); }
  };

  // Filtered + sorted resources
  const resources = React.useMemo(() => {
    let list = data?.resources || [];
    if (search) {
      const q = search.toLowerCase();
      list = list.filter(r => r.name.toLowerCase().includes(q) || r.email.toLowerCase().includes(q));
    }
    if (statusFilter === "active") list = list.filter(r => activityLevel(r.last_translator_update).label === "Active");
    if (statusFilter === "idle") list = list.filter(r => activityLevel(r.last_translator_update).label === "Idle");
    if (statusFilter === "issues") list = list.filter(r => r.issue_flagged > 0);
    if (statusFilter === "no_activity") list = list.filter(r => !r.last_translator_update);
    const dir = sortDir === "desc" ? -1 : 1;
    list = [...list].sort((a, b) => ((a[sortBy] || 0) - (b[sortBy] || 0)) * dir);
    return list;
  }, [data, search, statusFilter, sortBy, sortDir]);

  // Filtered detail tasks
  const filteredDetailTasks = React.useMemo(() => {
    let tasks = detailTasks;
    if (detailSearch) {
      const q = detailSearch.toLowerCase();
      tasks = tasks.filter(t => (t.main_title || "").toLowerCase().includes(q) || (t.episode_title || "").toLowerCase().includes(q));
    }
    if (detailFilter.project) tasks = tasks.filter(t => t.main_title === detailFilter.project);
    if (detailFilter.status) tasks = tasks.filter(t => t.task_status === detailFilter.status);
    if (detailFilter.type) tasks = tasks.filter(t => t.task === detailFilter.type);
    return tasks;
  }, [detailTasks, detailSearch, detailFilter]);

  const detailProjects = [...new Set(detailTasks.map(t => t.main_title).filter(Boolean))].sort();
  const detailTypes = [...new Set(detailTasks.map(t => t.task).filter(Boolean))].sort();

  const SH = ({ label, field, align = "center" }) => (
    <th className={`px-3 py-2.5 text-${align} text-[10px] font-semibold uppercase tracking-wider text-slate-400 cursor-pointer hover:text-blue-500 transition-colors whitespace-nowrap`}
      onClick={() => toggleSort(field)}>
      <div className={`flex items-center gap-1 ${align === "center" ? "justify-center" : align === "right" ? "justify-end" : ""}`}>
        {label}{sortBy === field ? (sortDir === "asc" ? <ArrowUp className="w-3 h-3 text-blue-500" /> : <ArrowDown className="w-3 h-3 text-blue-500" />) : <ArrowUpDown className="w-3 h-3 opacity-30" />}
      </div>
    </th>
  );

  // ═══════ DETAIL VIEW ═══════
  if (selectedResource) {
    const r = selectedResource;
    const act = activityLevel(r.last_translator_update);
    return (
      <div className="p-6" data-testid="resource-ops-detail">
        {/* Header */}
        <div className="flex items-center gap-4 mb-6">
          <Button variant="outline" size="sm" onClick={() => setSelectedResource(null)} data-testid="back-to-list">
            <ChevronLeft className="w-4 h-4 mr-1" />Back
          </Button>
          <div className="flex items-center gap-3 flex-1">
            <div className={`w-12 h-12 rounded-full flex items-center justify-center text-lg font-bold text-white ${act.ring}`}
              style={{ backgroundColor: `hsl(${[...(r.email || "")].reduce((a, c) => a + c.charCodeAt(0), 0) % 360}, 55%, 50%)` }}>
              {(r.name || "?").charAt(0).toUpperCase()}
            </div>
            <div>
              <h1 className="text-xl font-bold text-slate-900">{r.name}</h1>
              <p className="text-xs text-slate-500">{r.email}</p>
            </div>
            <div className={`w-2.5 h-2.5 rounded-full ${act.color}`} title={act.label} />
            <Badge className={`text-[10px] ${act.label === "Active" ? "bg-emerald-100 text-emerald-700" : act.label === "Recent" ? "bg-amber-100 text-amber-700" : "bg-slate-100 text-slate-500"}`}>{act.label}</Badge>
          </div>
          <Button size="sm" variant="outline" className="text-blue-600 border-blue-200" onClick={() => openEmailDialog(r)} data-testid="email-resource-btn">
            <Mail className="w-4 h-4 mr-1" />Email
          </Button>
        </div>

        {/* KPIs */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6">
          {[
            { label: "Total Tasks", val: r.total_tasks, color: "text-blue-600" },
            { label: "DB Completed", val: r.completed, color: "text-emerald-600" },
            { label: "In Progress", val: r.in_progress, color: "text-amber-600" },
            { label: "Issues Flagged", val: r.issue_flagged, color: "text-red-600" },
            { label: "Completion", val: `${r.completion_pct}%`, color: "text-violet-600" },
          ].map(k => (
            <div key={k.label} className="bg-white rounded-xl border p-3 text-center">
              <p className={`text-lg font-bold ${k.color}`}>{k.val}</p>
              <p className="text-[9px] text-slate-500 uppercase">{k.label}</p>
            </div>
          ))}
        </div>

        {/* Projects breakdown */}
        {r.projects?.length > 0 && (
          <div className="bg-white rounded-xl border p-4 mb-4">
            <p className="text-xs font-semibold text-slate-500 uppercase mb-2 flex items-center gap-1"><FolderOpen className="w-3.5 h-3.5" />Assigned Projects ({r.project_count})</p>
            <div className="flex flex-wrap gap-1.5">
              {r.projects.map(p => (
                <Badge key={p} className="text-[10px] bg-slate-100 text-slate-700 border-0 cursor-pointer hover:bg-blue-100 hover:text-blue-700"
                  onClick={() => setDetailFilter(prev => ({ ...prev, project: prev.project === p ? "" : p }))}>{p}</Badge>
              ))}
            </div>
          </div>
        )}

        {/* Task Filters */}
        <div className="bg-white rounded-xl border p-3 mb-4 flex items-center gap-2 flex-wrap">
          <div className="relative w-56">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" />
            <Input className="h-8 pl-8 text-[12px]" placeholder="Search tasks..." value={detailSearch} onChange={e => setDetailSearch(e.target.value)} />
          </div>
          {detailProjects.length > 0 && (
            <Select value={detailFilter.project || "all"} onValueChange={v => setDetailFilter(p => ({ ...p, project: v === "all" ? "" : v }))}>
              <SelectTrigger className="h-8 w-40 text-[12px]"><SelectValue placeholder="All Projects" /></SelectTrigger>
              <SelectContent><SelectItem value="all">All Projects</SelectItem>{detailProjects.map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
            </Select>
          )}
          <Select value={detailFilter.status || "all"} onValueChange={v => setDetailFilter(p => ({ ...p, status: v === "all" ? "" : v }))}>
            <SelectTrigger className="h-8 w-32 text-[12px]"><SelectValue placeholder="All Status" /></SelectTrigger>
            <SelectContent><SelectItem value="all">All Status</SelectItem><SelectItem value="Completed">Completed</SelectItem><SelectItem value="In Progress">In Progress</SelectItem></SelectContent>
          </Select>
          {detailTypes.length > 0 && (
            <Select value={detailFilter.type || "all"} onValueChange={v => setDetailFilter(p => ({ ...p, type: v === "all" ? "" : v }))}>
              <SelectTrigger className="h-8 w-28 text-[12px]"><SelectValue placeholder="All Types" /></SelectTrigger>
              <SelectContent><SelectItem value="all">All Types</SelectItem>{detailTypes.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
            </Select>
          )}
          {(detailSearch || detailFilter.project || detailFilter.status || detailFilter.type) && (
            <Button variant="ghost" size="sm" className="h-8 text-[11px] text-red-500" onClick={() => { setDetailSearch(""); setDetailFilter({ project: "", status: "", type: "" }); }}>
              <X className="w-3.5 h-3.5 mr-1" />Clear
            </Button>
          )}
          <span className="ml-auto text-[11px] text-slate-400">{filteredDetailTasks.length} tasks</span>
        </div>

        {/* Tasks Table */}
        <div className="bg-white rounded-xl border overflow-hidden">
          {detailLoading ? (
            <div className="flex items-center justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-blue-400" /></div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-slate-50 border-b">
                  <tr>
                    <th className="px-3 py-2 text-left text-[10px] font-semibold uppercase text-slate-400">#</th>
                    <th className="px-3 py-2 text-left text-[10px] font-semibold uppercase text-slate-400">Project</th>
                    <th className="px-3 py-2 text-left text-[10px] font-semibold uppercase text-slate-400">Episode</th>
                    <th className="px-3 py-2 text-center text-[10px] font-semibold uppercase text-slate-400">Task</th>
                    <th className="px-3 py-2 text-center text-[10px] font-semibold uppercase text-slate-400">Language</th>
                    <th className="px-3 py-2 text-center text-[10px] font-semibold uppercase text-slate-400">Due Date</th>
                    <th className="px-3 py-2 text-center text-[10px] font-semibold uppercase text-slate-400">Duration</th>
                    <th className="px-3 py-2 text-center text-[10px] font-semibold uppercase text-slate-400">DB Status</th>
                    <th className="px-3 py-2 text-center text-[10px] font-semibold uppercase text-slate-400">Live Status</th>
                    <th className="px-3 py-2 text-right text-[10px] font-semibold uppercase text-slate-400">Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredDetailTasks.map((t, i) => (
                    <tr key={t.id || i} className={`border-b hover:bg-slate-50 transition-colors ${t.translator_status === "issue_flagged" ? "bg-red-50/30" : ""}`}>
                      <td className="px-3 py-2 text-[11px] text-slate-400">{i + 1}</td>
                      <td className="px-3 py-2 text-[12px] font-medium text-slate-700 truncate max-w-[160px]">{t.main_title || "-"}{t.season_number ? <span className="text-[10px] text-slate-400 ml-1">S{t.season_number}</span> : ""}</td>
                      <td className="px-3 py-2 text-[12px] text-slate-500 truncate max-w-[140px]">{t.episode_title || "-"}</td>
                      <td className="px-3 py-2 text-center"><Badge variant="outline" className="text-[10px]">{t.task || "-"}</Badge></td>
                      <td className="px-3 py-2 text-center text-[12px] text-slate-500">{t.language || "-"}</td>
                      <td className="px-3 py-2 text-center text-[11px] text-slate-500">{t.due_date || "-"}</td>
                      <td className="px-3 py-2 text-center text-[12px] text-slate-500">{t.duration_words || "-"}</td>
                      <td className="px-3 py-2 text-center">
                        <Badge className={`border-0 text-[10px] ${t.task_status === "Completed" ? "bg-emerald-50 text-emerald-700" : "bg-amber-50 text-amber-700"}`}>
                          {t.task_status || "In Progress"}
                        </Badge>
                      </td>
                      <td className="px-3 py-2 text-center">
                        {t.translator_status ? (
                          <Badge className={`border-0 text-[10px] ${statusColors[t.translator_status] || "bg-slate-100 text-slate-500"}`}>
                            {t.translator_status === "completed" ? "Completed" : t.translator_status === "in_progress" ? "In Progress" : t.translator_status === "issue_flagged" ? "Has Issues" : t.translator_status}
                          </Badge>
                        ) : <span className="text-[10px] text-slate-300">-</span>}
                      </td>
                      <td className="px-3 py-2 text-right text-[12px] font-semibold text-slate-700">{t.amount ? `\u20B9${t.amount.toLocaleString("en-IN")}` : "-"}</td>
                    </tr>
                  ))}
                  {filteredDetailTasks.length === 0 && !detailLoading && (
                    <tr><td colSpan={10} className="text-center py-10 text-sm text-slate-400">No tasks found</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    );
  }

  // ═══════ LIST VIEW ═══════
  const activeCount = (data?.resources || []).filter(r => activityLevel(r.last_translator_update).label === "Active").length;
  const issueCount = data?.total_issues || 0;

  return (
    <div className="p-6" data-testid="resource-ops-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: "Manrope, sans-serif" }}>Resource Operations</h1>
          <p className="text-sm text-slate-500 mt-0.5">Live monitoring of all resources and their assigned work</p>
        </div>
        <Button variant="outline" size="sm" onClick={fetchData}><RefreshCw className="w-4 h-4 mr-1" />Refresh</Button>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
        <div className="bg-gradient-to-br from-blue-500 to-indigo-600 rounded-2xl p-5 text-white">
          <Users className="w-6 h-6 mb-2 opacity-70" />
          <p className="text-2xl font-bold">{data?.total_resources || 0}</p>
          <p className="text-xs text-white/70">Total Resources</p>
        </div>
        <div className="bg-gradient-to-br from-emerald-500 to-teal-600 rounded-2xl p-5 text-white">
          <Activity className="w-6 h-6 mb-2 opacity-70" />
          <p className="text-2xl font-bold">{activeCount}</p>
          <p className="text-xs text-white/70">Active (24h)</p>
        </div>
        <div className="bg-gradient-to-br from-violet-500 to-purple-600 rounded-2xl p-5 text-white">
          <CheckCircle2 className="w-6 h-6 mb-2 opacity-70" />
          <p className="text-2xl font-bold">{data?.total_translator_completed || 0}</p>
          <p className="text-xs text-white/70">Marked Complete</p>
        </div>
        <div className="bg-gradient-to-br from-red-500 to-rose-600 rounded-2xl p-5 text-white">
          <AlertTriangle className="w-6 h-6 mb-2 opacity-70" />
          <p className="text-2xl font-bold">{issueCount}</p>
          <p className="text-xs text-white/70">Issues Flagged</p>
        </div>
        <div className="bg-gradient-to-br from-amber-500 to-orange-600 rounded-2xl p-5 text-white">
          <Clock className="w-6 h-6 mb-2 opacity-70" />
          <p className="text-2xl font-bold">{(data?.resources || []).filter(r => !r.last_translator_update).length}</p>
          <p className="text-xs text-white/70">No Activity</p>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl border p-3 mb-4 flex items-center gap-2 flex-wrap">
        <div className="relative w-64">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" />
          <Input className="h-8 pl-8 text-[12px]" placeholder="Search resource..." value={search} onChange={e => setSearch(e.target.value)} data-testid="ops-search" />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="h-8 w-36 text-[12px]"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Resources</SelectItem>
            <SelectItem value="active">Active (24h)</SelectItem>
            <SelectItem value="idle">Idle</SelectItem>
            <SelectItem value="issues">Has Issues</SelectItem>
            <SelectItem value="no_activity">No Activity</SelectItem>
          </SelectContent>
        </Select>
        <span className="text-[11px] text-slate-400 ml-auto">{resources.length} of {data?.total_resources || 0}</span>
      </div>

      {/* Resource Table */}
      <div className="bg-white rounded-xl border overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-blue-400" /></div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-50 border-b">
                <tr>
                  <th className="px-3 py-2.5 text-left text-[10px] font-semibold uppercase tracking-wider text-slate-400">#</th>
                  <th className="px-3 py-2.5 text-left text-[10px] font-semibold uppercase tracking-wider text-slate-400">Resource</th>
                  <SH label="Tasks" field="total_tasks" />
                  <SH label="Projects" field="project_count" />
                  <th className="px-3 py-2.5 text-center text-[10px] font-semibold uppercase tracking-wider text-slate-400">DB Status</th>
                  <th className="px-3 py-2.5 text-center text-[10px] font-semibold uppercase tracking-wider text-slate-400">Live Status</th>
                  <SH label="Issues" field="issue_flagged" />
                  <SH label="Completion" field="completion_pct" />
                  <th className="px-3 py-2.5 text-center text-[10px] font-semibold uppercase tracking-wider text-slate-400">Activity</th>
                  <th className="px-3 py-2.5 text-center text-[10px] font-semibold uppercase tracking-wider text-slate-400">Actions</th>
                </tr>
              </thead>
              <tbody>
                {resources.map((r, i) => {
                  const act = activityLevel(r.last_translator_update);
                  return (
                    <tr key={r.email} className={`border-b hover:bg-slate-50 transition-colors cursor-pointer ${r.issue_flagged > 0 ? "bg-red-50/20" : ""}`}
                      onClick={() => openDetail(r)} data-testid={`ops-row-${r.email}`}>
                      <td className="px-3 py-2.5 text-[11px] text-slate-400">{i + 1}</td>
                      <td className="px-3 py-2.5">
                        <div className="flex items-center gap-2.5">
                          <div className="relative">
                            <div className="w-8 h-8 rounded-full flex items-center justify-center text-[11px] font-bold text-white"
                              style={{ backgroundColor: `hsl(${[...(r.email || "")].reduce((a, c) => a + c.charCodeAt(0), 0) % 360}, 55%, 50%)` }}>
                              {(r.name || "?").charAt(0).toUpperCase()}
                            </div>
                            <div className={`absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full border-2 border-white ${act.color}`} />
                          </div>
                          <div>
                            <p className="text-[12px] font-medium text-slate-800">{r.name}</p>
                            <p className="text-[10px] text-slate-400">{r.email}</p>
                          </div>
                        </div>
                      </td>
                      <td className="px-3 py-2.5 text-center text-[12px] font-semibold text-slate-700">{r.total_tasks}</td>
                      <td className="px-3 py-2.5 text-center text-[11px] text-slate-600">{r.project_count}</td>
                      <td className="px-3 py-2.5 text-center">
                        <div className="flex items-center justify-center gap-1 text-[10px]">
                          <span className="text-emerald-600 font-medium">{r.completed}</span>
                          <span className="text-slate-300">/</span>
                          <span className="text-amber-600">{r.in_progress}</span>
                        </div>
                      </td>
                      <td className="px-3 py-2.5 text-center">
                        <div className="flex items-center justify-center gap-1">
                          {r.translator_completed > 0 && <Badge className="bg-emerald-100 text-emerald-700 border-0 text-[9px]">{r.translator_completed}</Badge>}
                          {r.translator_in_progress > 0 && <Badge className="bg-blue-100 text-blue-700 border-0 text-[9px]">{r.translator_in_progress}</Badge>}
                          {!r.translator_completed && !r.translator_in_progress && <span className="text-slate-300 text-[10px]">-</span>}
                        </div>
                      </td>
                      <td className="px-3 py-2.5 text-center">
                        {r.issue_flagged > 0 ? (
                          <Badge className="bg-red-100 text-red-700 border-0 text-[10px]"><AlertTriangle className="w-3 h-3 mr-0.5" />{r.issue_flagged}</Badge>
                        ) : <span className="text-slate-300 text-[10px]">-</span>}
                      </td>
                      <td className="px-3 py-2.5 text-center">
                        <div className="flex items-center justify-center gap-1">
                          <div className="w-14 h-1.5 bg-slate-100 rounded-full overflow-hidden">
                            <div className={`h-full rounded-full ${r.completion_pct >= 90 ? "bg-emerald-500" : r.completion_pct >= 50 ? "bg-amber-500" : "bg-red-500"}`}
                              style={{ width: `${r.completion_pct}%` }} />
                          </div>
                          <span className="text-[10px] text-slate-500 w-8">{r.completion_pct}%</span>
                        </div>
                      </td>
                      <td className="px-3 py-2.5 text-center">
                        <div className="flex items-center justify-center gap-1">
                          <div className={`w-2 h-2 rounded-full ${act.color}`} />
                          <span className="text-[10px] text-slate-500">{act.label}</span>
                        </div>
                      </td>
                      <td className="px-3 py-2.5 text-center" onClick={e => e.stopPropagation()}>
                        <div className="flex items-center justify-center gap-1">
                          <Button variant="ghost" size="sm" className="h-6 w-6 p-0 text-blue-500" onClick={() => openEmailDialog(r)} title="Email">
                            <Mail className="w-3.5 h-3.5" />
                          </Button>
                          <Button variant="ghost" size="sm" className="h-6 w-6 p-0 text-slate-500" onClick={() => openDetail(r)} title="View Tasks">
                            <Eye className="w-3.5 h-3.5" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Email Dialog */}
      <Dialog open={showEmail} onOpenChange={setShowEmail}>
        <DialogContent className="max-w-lg" data-testid="email-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Mail className="w-5 h-5 text-blue-600" />Email Resource</DialogTitle>
            <DialogDescription>Send email to {emailTarget?.name} ({emailTarget?.email})</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 mt-2">
            <div>
              <label className="text-[10px] font-medium text-slate-500 uppercase mb-1 block">Subject</label>
              <Input value={emailSubject} onChange={e => setEmailSubject(e.target.value)} className="h-8 text-sm" />
            </div>
            <div>
              <label className="text-[10px] font-medium text-slate-500 uppercase mb-1 block">Message</label>
              <textarea value={emailBody} onChange={e => setEmailBody(e.target.value)} rows={6}
                className="w-full rounded-md border border-slate-200 p-3 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500" />
            </div>
            <Button className="w-full bg-blue-600 hover:bg-blue-700" onClick={handleSendEmail} disabled={sendingEmail || !emailSubject}>
              {sendingEmail ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Send className="w-4 h-4 mr-1" />}
              {sendingEmail ? "Sending..." : "Send Email"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
